/**
 * Deck import utilities
 *
 * Supports common deck list formats:
 * - MTGA format: 4 Lightning Bolt (M10) 146
 * - Moxfield format: Similar with zone headers
 * - Simple text: 4 Lightning Bolt
 * - Dragon Shield CSV: Card Type,Quantity,Card Name,Mana Cost,Min Price
 * - Text Summary: MtgCraft grouped format with // Section (count) headers
 *
 * Deck exporters live in @/features/import-export/exporters/deck*.ts
 */
import { parseCardLine as sharedParseCardLine } from '@/lib/cardLineParser';
import type { Finish } from '@/types/card';
import type { DeckCard, DeckDisplayCard, DeckZone } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface DeckImportCard {
  name: string;
  setCode: string | null;
  collectorNumber: string | null;
  quantity: number;
  zone: DeckZone;
  /** Scryfall ID (provided by MtgCraft backup imports) */
  scryfallId?: string;
  /** Finish parsed from foil markers (e.g. *F* → foil, *E* → etched) */
  finish?: Finish;
}

export interface DeckImportResult {
  name: string | null;
  format: string | null;
  cards: DeckImportCard[];
  errors: string[];
  /** Whether the source explicitly defined zone headers (sideboard, commander, etc.) */
  hasZoneDefinitions: boolean;
}

/** Card data needed for export (minimal subset) */
export interface ExportableDeckCard extends DeckCard {
  card: DeckDisplayCard;
}

// ============================================================================
// Parser
// ============================================================================

/**
 * Parse a deck list string into structured data.
 * Supports multiple formats with zone headers.
 */
export function parseDeckList(text: string): DeckImportResult {
  const lines = text.trim().split(/\r?\n/);
  const cards: DeckImportCard[] = [];
  const errors: string[] = [];

  let currentZone: DeckZone = 'main';
  let deckName: string | null = null;
  let deckFormat: string | null = null;
  let hasZoneDefinitions = false;

  // Track blank line positions for MTGO commander detection
  let lastBlankLineIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const lineNum = i + 1;

    // Skip empty lines (but track them for MTGO detection)
    if (!trimmed) {
      lastBlankLineIndex = cards.length; // track position relative to parsed cards
      continue;
    }

    // Arena preamble: skip "About" line
    if (/^About$/i.test(trimmed)) {
      continue;
    }

    // Arena preamble: extract deck name from "Name {text}" line
    const arenaNameMatch = trimmed.match(/^Name\s+(.+)$/i);
    if (arenaNameMatch) {
      if (!deckName) {
        deckName = arenaNameMatch[1].trim();
      }
      continue;
    }

    // Check for deck name comment (Text Summary format): "// Deck: Name"
    const deckNameMatch = trimmed.match(/^\/\/\s*Deck:\s*(.+)$/i);
    if (deckNameMatch) {
      deckName = deckNameMatch[1].trim();
      continue;
    }

    // Check for format comment: "// Format: Commander"
    const formatMatch = trimmed.match(/^(?:\/\/|#)\s*Format:\s*(.+)$/i);
    if (formatMatch) {
      deckFormat = formatMatch[1].trim();
      continue;
    }

    // Check for total cards comment (skip): "// Cards: 100"
    if (/^\/\/\s*Cards:\s*\d+$/i.test(trimmed)) {
      continue;
    }

    // Check for deck name (first line without pattern, not a zone header)
    if (i === 0 && !trimmed.match(/^\d/) && !isZoneHeader(trimmed) && !trimmed.startsWith('//') && !trimmed.startsWith('#')) {
      const nameMatch = trimmed.match(/^(?:Deck:\s*)?(.+)$/i);
      if (nameMatch) {
        deckName = nameMatch[1].trim();
        continue;
      }
    }

    // Skip other comments (that aren't zone headers)
    if ((trimmed.startsWith('//') || trimmed.startsWith('#')) && !parseZoneHeader(trimmed)) {
      continue;
    }

    // Check for zone headers
    const zone = parseZoneHeader(trimmed);
    if (zone) {
      currentZone = zone;
      // Track that zones were explicitly defined (except 'main' as that's the default)
      if (zone !== 'main') {
        hasZoneDefinitions = true;
      }
      continue;
    }

    // Try to parse card line
    const parsed = sharedParseCardLine(trimmed);
    if (parsed) {
      cards.push({
        name: parsed.name,
        setCode: parsed.setCode,
        collectorNumber: parsed.collectorNumber,
        quantity: parsed.quantity,
        zone: currentZone,
        ...(parsed.finish !== 'nonfoil' && { finish: parsed.finish }),
      });
    } else {
      errors.push(`Line ${lineNum}: Could not parse "${trimmed}"`);
    }
  }

  // MTGO commander detection: if no zone headers were defined and there's a
  // blank line gap before trailing card(s), assign those trailing cards to commander
  if (!hasZoneDefinitions && lastBlankLineIndex > 0 && lastBlankLineIndex < cards.length) {
    for (let i = lastBlankLineIndex; i < cards.length; i++) {
      cards[i].zone = 'commander';
    }
    hasZoneDefinitions = true;
  }

  return {
    name: deckName,
    format: deckFormat,
    cards,
    errors,
    hasZoneDefinitions,
  };
}

// ============================================================================
// Zone header parsing (used by parseDeckList)
// ============================================================================
function isZoneHeader(line: string): boolean {
  return parseZoneHeader(line) !== null;
}

/**
 * Parse a zone header line.
 * Made robust to handle various export format variations.
 */
function parseZoneHeader(line: string): DeckZone | null {
  // Normalize: lowercase, remove extra whitespace
  const normalized = line.toLowerCase().trim();

  // Common zone header patterns - ordered from most specific to least
  const patterns: { pattern: RegExp; zone: DeckZone }[] = [
    // Text Summary format: "// Commander (1)", "// Sideboard (15)"
    { pattern: /^\/\/\s*commander\s*\(\d+\)\s*$/i, zone: 'commander' },
    { pattern: /^\/\/\s*companion\s*\(\d+\)\s*$/i, zone: 'companion' },
    { pattern: /^\/\/\s*sideboard\s*\(\d+\)\s*$/i, zone: 'sideboard' },
    { pattern: /^\/\/\s*outside\s*the\s*game\s*\(\d+\)\s*$/i, zone: 'maybeboard' },
    { pattern: /^\/\/\s*maybeboard\s*\(\d+\)\s*$/i, zone: 'maybeboard' },
    { pattern: /^\/\/\s*tokens?\s*\(\d+\)\s*$/i, zone: 'tokens' },
    // Text Summary: Any other // Category (count) header → main deck
    {
      pattern:
        /^\/\/\s*(?:creatures?|planeswalkers?|artifacts?|enchantments?|instants?|sorceries|sorcery|battles?|lands?|other)\s*\(\d+\)\s*$/i,
      zone: 'main',
    },

    // Moxfield style with count: "Commander (1)", "Deck (99)"
    { pattern: /^commander\s*\(\d+\)\s*$/i, zone: 'commander' },
    { pattern: /^companion\s*\(\d+\)\s*$/i, zone: 'companion' },
    { pattern: /^deck\s*\(\d+\)\s*$/i, zone: 'main' },
    { pattern: /^mainboard\s*\(\d+\)\s*$/i, zone: 'main' },
    { pattern: /^sideboard\s*\(\d+\)\s*$/i, zone: 'sideboard' },
    { pattern: /^maybeboard\s*\(\d+\)\s*$/i, zone: 'maybeboard' },
    { pattern: /^tokens?\s*\(\d+\)\s*$/i, zone: 'tokens' },

    // MTGA Arena style with // prefix
    { pattern: /^\/\/\s*commander\s*$/i, zone: 'commander' },
    { pattern: /^\/\/\s*companion\s*$/i, zone: 'companion' },
    { pattern: /^\/\/\s*(?:deck|mainboard)\s*$/i, zone: 'main' },
    { pattern: /^\/\/\s*sideboard\s*$/i, zone: 'sideboard' },

    // Simple headers with optional colon: "Commander", "Deck:", "Sideboard"
    { pattern: /^commander:?\s*$/i, zone: 'commander' },
    { pattern: /^companion:?\s*$/i, zone: 'companion' },
    { pattern: /^(?:main\s*deck|mainboard|deck):?\s*$/i, zone: 'main' },
    { pattern: /^sideboard:?\s*$/i, zone: 'sideboard' },
    { pattern: /^maybeboard:?\s*$/i, zone: 'maybeboard' },
    { pattern: /^tokens?:?\s*$/i, zone: 'tokens' },
  ];

  for (const { pattern, zone } of patterns) {
    if (pattern.test(line)) {
      return zone;
    }
  }

  // Fallback: check if line is ONLY a zone keyword (no card data)
  // This catches edge cases where formatting is slightly off
  const zoneKeywords: Record<string, DeckZone> = {
    commander: 'commander',
    companion: 'companion',
    deck: 'main',
    mainboard: 'main',
    'main deck': 'main',
    sideboard: 'sideboard',
    maybeboard: 'maybeboard',
    token: 'tokens',
    tokens: 'tokens',
  };

  // Strip any trailing characters that aren't alphanumeric
  const cleanedLine = normalized.replace(/[^a-z\s]/g, '').trim();
  if (zoneKeywords[cleanedLine]) {
    return zoneKeywords[cleanedLine];
  }

  return null;
}

// ============================================================================
// Format Detection
// ============================================================================

/**
 * Detect the deck list format from text content.
 */
export function detectDeckFormat(text: string): 'mtga' | 'moxfield' | 'mtgo' | 'arena' | 'simple' | 'unknown' {
  const lines = text.trim().split(/\r?\n/);

  // Check for Arena preamble (About / Name lines)
  if (lines.some((l) => /^About$/i.test(l.trim())) && lines.some((l) => /^Name\s+.+$/i.test(l.trim()))) {
    return 'arena';
  }

  // Check for Moxfield-style headers: "Zone (count)"
  if (lines.some((l) => /^\w+\s+\(\d+\)$/i.test(l.trim()))) {
    return 'moxfield';
  }

  // Check for MTGA-style with set codes
  if (lines.some((l) => /\([A-Za-z0-9]+\)\s+\S+$/.test(l.trim()))) {
    return 'mtga';
  }

  // Check for MTGO style: flat card list with trailing blank line + card(s) at end
  // (no zone headers, blank line before last few cards)
  const nonEmpty = lines.filter((l) => l.trim().length > 0);
  if (nonEmpty.length > 2) {
    // Find if there's a blank line followed by 1-2 card lines at the very end
    for (let i = lines.length - 1; i >= 0; i--) {
      const trimmed = lines[i].trim();
      if (!trimmed) {
        // Verify there are cards both before and after this blank line
        const cardsBefore = lines.slice(0, i).some((l) => /^\d+\s+\w+/.test(l.trim()));
        const cardsAfter = lines.slice(i + 1).some((l) => /^\d+\s+\w+/.test(l.trim()));
        if (cardsBefore && cardsAfter) {
          // Count trailing cards after blank (should be 1-2 for commanders)
          const trailingCards = lines.slice(i + 1).filter((l) => l.trim().length > 0);
          if (trailingCards.length >= 1 && trailingCards.length <= 2) {
            // Ensure no zone headers exist
            const hasHeaders = lines.some((l) => parseZoneHeader(l.trim()) !== null);
            if (!hasHeaders) {
              return 'mtgo';
            }
          }
        }
        break;
      }
      // Only allow card lines after the blank
      if (!/^\d+\s+\w+/.test(trimmed)) break;
    }
  }

  // Check for simple format (just quantities and names)
  if (lines.some((l) => /^\d+\s+\w+/.test(l.trim()))) {
    return 'simple';
  }

  return 'unknown';
}
